import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import {
  Button,
  KPICard,
  ConfirmDialog,
  ActionToolbar,
  DataNavigator,
  StatusBadge,
  Badge
} from '@/components/ui';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useToast } from '@/components/ui/Toast';
import { contractStatusMap, contractTypeMap } from '@/utils/status';

// Internal
import { useConstructionContracts } from './hooks/useConstructionContracts';
import { ContractFormModal } from './components/ContractFormModal';
import type { ConstructionContract } from './types/construction-contracts.types';
import {
  FilePlus,
  FileText,
  DollarSign,
  Layers,
  Percent,
  Download,
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Building2,
  MoreVertical
} from 'lucide-react';

export default function ContractsPage() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const companyId = profile?.company_id || '';

  const [showCreate, setShowCreate] = useState(false);
  const [editContract, setEditContract] = useState<ConstructionContract | null>(null);
  const [deleteContract, setDeleteContract] = useState<ConstructionContract | null>(null);

  const {
    contracts,
    stats,
    isLoading,
    filters,
    setFilters,
    deleteContract: executeDelete,
    isDeleting
  } = useConstructionContracts(companyId);

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteContract) return;
    try {
      await executeDelete(deleteContract.id);
      setDeleteContract(null);
      toast.success('Đã xóa hợp đồng thành công');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  const columns = useMemo(() => [
    {
      key: 'contract_info',
      title: 'Hợp đồng',
      render: (_: any, item: ConstructionContract) => (
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-slate-400  tracking-widest">{item.contract_code}</span>
          <span className="font-black text-slate-900 leading-tight">{item.contract_name}</span>
          <span className="text-[10px] text-slate-400 font-bold  tracking-tighter mt-0.5">{item.contract_type}</span>
        </div>
      )
    },
    {
      key: 'partner',
      title: 'Đối tác / Dự án',
      render: (_: any, item: ConstructionContract) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <Building2 className="w-3 h-3 text-slate-400" />
            <span className="text-xs font-bold text-slate-700">{item.contractor_name || 'Chưa xác định'}</span>
          </div>
          <span className="text-[10px] text-slate-400 font-black  tracking-tighter mt-0.5">{item.project?.name || '---'}</span>
        </div>
      )
    },
    {
      key: 'value',
      title: 'Giá trị',
      render: (_: any, item: ConstructionContract) => (
        <div className="flex flex-col">
          <span className="font-black text-blue-600">
            {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(item.contract_value)}
          </span>
          <div className="flex items-center gap-1.5">
             <Percent className="w-2.5 h-2.5 text-slate-300" />
             <span className="text-[10px] text-slate-400 font-bold ">Giữ lại: {item.retention_rate}%</span>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      title: 'Trạng thái',
      render: (_: any, item: ConstructionContract) => (
        <StatusBadge status={item.status_code} type="default" className="font-black" />
      )
    },
    {
      key: 'actions',
      title: '',
      align: 'right' as const,
      render: (_: any, item: ConstructionContract) => (
        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" onClick={() => navigate(`./${item.id}`)}>
            <Eye className="w-4 h-4" />
          </Button>
          <AuthButton
            module="boq"
            action="update"
            variant="ghost"
            size="sm"
            onClick={() => setEditContract(item)}
          >
            <Edit className="w-4 h-4" />
          </AuthButton>
          <AuthButton
            module="boq"
            action="delete"
            variant="ghost"
            size="sm"
            className="text-red-500 hover:bg-red-50"
            onClick={() => setDeleteContract(item)}
          >
            <Trash2 className="w-4 h-4" />
          </AuthButton>
        </div>
      )
    }
  ], [navigate]);

  const mobileCardRender = (item: ConstructionContract) => (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-slate-400  tracking-widest">{item.contract_code}</span>
          <h3 className="font-black text-slate-900 text-lg leading-tight">{item.contract_name}</h3>
          <Badge variant="outline" className="mt-1 text-[10px] px-2  tracking-widest font-black text-blue-600">{item.contract_type}</Badge>
        </div>
        <StatusBadge status={item.status_code} type="default" size="sm" />
      </div>

      <div className="bg-white/50 p-3 rounded-2xl border border-white shadow-sm space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <Building2 className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-bold text-slate-700">{item.contractor_name || 'Chưa xác định'}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-medium text-slate-500">{item.project?.name || '---'}</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="flex flex-col">
          <span className="text-[8px] font-black text-slate-400  tracking-widest">Tổng giá trị</span>
          <span className="text-sm font-black text-blue-600">
             {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(item.contract_value)}
          </span>
        </div>
        <div className="flex gap-2">
           <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-xl" onClick={() => navigate(`./${item.id}`)}>
             <Eye className="w-4 h-4 text-slate-500" />
           </Button>
           <AuthButton
              module="boq"
              action="update"
              variant="primary"
              size="sm"
              className="h-9 px-4 rounded-xl font-black text-[10px]  tracking-widest"
              onClick={() => setEditContract(item)}
            >
              Cập nhật
            </AuthButton>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Hợp đồng Thi công"
      description="Quản lý thỏa thuận pháp lý, phạm vi công việc và thanh toán"
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Hợp đồng' }]}
    >
      <div className="space-y-6">
        {/* KPI Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng giá trị HĐ"
            value={stats.totalValue}
            icon={DollarSign}
            variant="primary"
            format="compact-currency"
          />
          <KPICard
            title="Đang thực hiện"
            value={stats.activeContracts}
            icon={Layers}
            variant="success"
          />
          <KPICard
            title="Chờ phê duyệt"
            value={stats.pendingValue}
            icon={FileText}
            variant="warning"
            format="compact-currency"
          />
          <KPICard
            title="Tỉ lệ giữ lại TB"
            value={stats.avgRetentionRate}
            icon={Percent}
            variant="info"
            suffix="%"
          />
        </div>

        {/* Action Toolbar */}
        <ActionToolbar
          searchValue={filters.search}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Tìm mã HĐ, tên hợp đồng, đối tác..."
          filterOptions={[
            {
              id: 'status', label: 'Trạng thái', type: 'select', choices: [
                { value: 'all', label: 'Tất cả trạng thái' },
                ...Object.entries(contractStatusMap).map(([val, cfg]) => ({ value: val, label: cfg.label }))
              ]
            },
            {
              id: 'type', label: 'Loại hình', type: 'select', choices: [
                { value: 'all', label: 'Tất cả loại' },
                ...Object.entries(contractTypeMap).map(([val, cfg]) => ({ value: val, label: cfg.label }))
              ]
            }
          ]}
          onFilterChange={handleFilterChange}
          actions={
            <Button variant="outline" size="md" className="rounded-2xl mr-2">
              <Download className="w-4 h-4 mr-2" />
              Báo cáo
            </Button>
          }
          primaryActionLabel="Tạo hợp đồng"
          primaryActionIcon={<Plus className="w-4 h-4" />}
          onPrimaryAction={() => setShowCreate(true)}
          isLoading={isLoading}
        />

        {/* Data Navigator */}
        <DataNavigator
          columns={columns}
          data={contracts}
          isLoading={isLoading}
          onRowClick={(item) => navigate(`./${item.id}`)}
          mobileCardRender={mobileCardRender}
        />
      </div>

      <ContractFormModal
        isOpen={showCreate || !!editContract}
        contract={editContract}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditContract(null);
        }}
      />

      <ConfirmDialog
        isOpen={!!deleteContract}
        title="Xác nhận xóa hợp đồng"
        message={`Bạn có chắc chắn muốn xóa hợp đồng "${deleteContract?.contract_name}"? Hành động này không thể hoàn tác.`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteContract(null)}
      />
    </PageLayout>
  );
}
